// src/components/ProductCard.jsx
import React from "react";
import { deleteFormulation } from "../data/store";
import { useRole } from "../data/useRole";
import "./ProductCard.css";

const BASE_LABELS = {
  W: "White Base",
  D: "Deep Base",
  B: "Mid Base",
  C: "Clear Base",
};

// Helper for consistent "empty" rendering
const dash = <span style={{ color: "#bbb" }}>-</span>;

function toPSLText(item) {
  let dateObj;
  if (item.createdAt?.toDate) {
    dateObj = item.createdAt.toDate();
  } else if (typeof item.createdAt === "string" || typeof item.createdAt === "number") {
    dateObj = new Date(item.createdAt);
  } else if (item.createdAt instanceof Date) {
    dateObj = item.createdAt;
  } else {
    dateObj = new Date();
  }
  const createdAt = dateObj.toLocaleString("en-GB");
  const creatorEmail = item.creatorEmail || "";

  const headerLines = [
    `NAME: ${item.name || ""}`,
    `CODE: ${item.code || ""}`,
    `PRODUCT: ${item.product || ""}`,
    `BASE: ${item.base || ""}`,
    `SIZE_L: ${item.sizeL || ""}`,
    `BASE_TYPE: ${item.baseType || ""}`,
    `CONFIDENCE: ${item.confidence || ""}`,
    item.notes ? `NOTES: ${item.notes}` : "NOTES:",
    `CREATED_AT: ${createdAt}`,
    `CREATOR_EMAIL: ${creatorEmail}`,
  ].filter(Boolean);

  const rowsLines = (item.rows || [])
    .filter((r) => r.colorant?.trim() && String(r.value ?? "").trim()) // ✅ Only include non-empty rows
    .map((r) => `${r.colorant.trim()} ${Number(r.value || 0)}`);

  return [...headerLines, "------", "Formula:", ...rowsLines].join("\n");
}

export default function ProductCard({ item, onRemove, onConvert }) {
  const { canEdit } = useRole();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(toPSLText(item));
    } catch {
      console.error("Copy failed");
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (!canEdit) return;
    if (window.confirm(`Delete "${item.name || "Untitled"}"?`)) {
      deleteFormulation(item.id).then(() => {
        if (onRemove) onRemove(item.id);
      });
    }
  };

  const handleConvert = (e) => {
    e.stopPropagation();
    if (onConvert) onConvert(item);
  };

  // Logic for Base Type display
  let displayBaseType = item.baseType || "";
  if (displayBaseType === "Base10 (New)") {
    displayBaseType = "Base10 (New, PSL Carousel)";
  }

  return (
    <article
      className="product-card-box"
      onClick={handleCopy}
      title="Click to copy details"
    >
      <div className="product-card-box__head">
        <h3 className="product-card-box__name">{item.name || dash}</h3>
      </div>

      <div style={{ fontSize: "1em", color: "#223", margin: "8px 0 10px 0" }}>
        <div><b>Code:</b> {item.code || dash}</div>
        <div><b>Product:</b> {item.product || dash}</div>
        <div>
          <b>Base:</b>{" "}
          {item.base
            ? `${item.base} (${BASE_LABELS[item.base] || "Unknown Base"})`
            : dash}
        </div>
        <div>
          <b>Size (L):</b>{" "}
          {item.sizeL !== undefined && item.sizeL !== "" ? item.sizeL : dash}
        </div>
        <div>
          <b>Base Type:</b>{" "}
          {displayBaseType !== "" ? displayBaseType : dash}
        </div>
        <div><b>Confidence:</b> {item.confidence || dash}</div>
        <div><b>Chart:</b> {item.chart || dash}</div>
        <div>
          <b>Notes:</b>{" "}
          {item.notes !== undefined && item.notes !== null && item.notes !== ""
            ? item.notes
            : dash}
        </div>
        <div>
          <b>Created:</b>{" "}
          {item.createdAt
            ? typeof item.createdAt === "object" && item.createdAt.toDate
              ? item.createdAt.toDate().toLocaleString("en-NZ")
              : new Date(item.createdAt).toLocaleString("en-NZ")
            : dash}
        </div>
        <div><b>Creator:</b> {item.creatorEmail || dash}</div>
      </div>

      {/* ✅ Only render non-empty rows */}
      {(item.rows && item.rows.some((r) => r.colorant?.trim() && String(r.value ?? "").trim())) && (
        <pre className="product-card-box__psl" style={{ margin: "8px 0" }}>
          {item.rows
            .filter((r) => r.colorant?.trim() && String(r.value ?? "").trim())
            .map((r) => `${r.colorant} = ${r.value}`)
            .join("\n")}
        </pre>
      )}

      <div className="product-card-box__actions" style={{ marginTop: 10 }}>
        <button
          className="product-card-box__btn"
          onClick={handleConvert}
          title="Convert formula"
        > 
          Convert
        </button>
        {canEdit && (
          <button
            className="product-card-box__btn product-card-box__btn--danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        )}
      </div>
    </article>
  );
}
